// Per-language label maps for BirthDetailsForm. Kept as a plain record (not inline
// ternaries) so the component stays simple and adding a label never grows cyclomatic
// complexity. Hindi controls are Devanagari Hindi (owner decision 2026-10-28, replacing
// the 2026-10-08 Hinglish rule); place-name examples stay Latin because the place search
// matches English spellings. Guarded by tests/hindi-register.test.ts (hi block carries
// Devanagari and no Latin words outside its allowlist; en block carries no Devanagari).

export const BIRTH_FORM_LABELS = {
  en: {
    yourName: "Your Name",
    enterFullName: "Enter your full name",
    namePlaceholderOptional: "Name (optional)",
    birthDate: "Birth Date",
    birthTime: "Birth Time",
    required: "(required)",
    optional: "(optional)",
    timeRequiredHint: "Ascendant changes every ~2 hours — accurate time is essential.",
    enterBirthTime: "Enter your birth time to continue with a full chart.",
    timeOptionalHint: "Skip if unknown. Lagna-based insights need exact time.",
    timeUnknownToggle: "Don't know your birth time?",
    timeUnknownExplainer:
      "No problem — leave it blank and we'll calculate from your birth date and place. Without the exact time we won't claim an exact Lagna, houses, divisional charts, or dasha timing, and the Moon can change rashi or nakshatra within a single day.",
    birthPlace: "Birth Place",
    changePlace: "Change",
    // Fits a 360 px phone input (~226 px of text); the longer example list
    // was cut to "…(e.g. Mu" (b-calc-07).
    placePlaceholder: "Type your city, e.g. Delhi",
    pickCityHint: "Pick a city from the suggestions for precise coordinates.",
    calculating: "Calculating...",
    yearTooEarly: "Year must be 1900 or later.",
    futureDate: "Birth date cannot be in the future.",
    invalidDate: "Please enter a valid birth date.",
    defaultSubmit: "Calculate",
    checkDetails: "Please check the birth details above and try again.",
    pickCityToContinue: "Pick your city from the suggestions to continue.",
    enterName: "Please enter your name.",
    enterBirthDate: "Please enter your birth date.",
    enterBirthPlace: "Please enter your birth city.",
    // No longer than pickCityHint: they share its reserved line(s) under the
    // field (BirthPlaceField), and calculator slots were measured with it.
    placeNoMatch: "No town found — try your district or nearest big town.",
    placeNoMatchDevanagari: "Hindi spelling not found — type in English, e.g. Sitapur.",
  },
  hi: {
    yourName: "आपका नाम",
    enterFullName: "अपना पूरा नाम लिखें",
    namePlaceholderOptional: "नाम (वैकल्पिक)",
    birthDate: "जन्म तिथि",
    birthTime: "जन्म समय",
    required: "(ज़रूरी)",
    optional: "(वैकल्पिक)",
    timeRequiredHint: "लग्न लगभग हर 2 घंटे में बदलता है — सही समय ज़रूरी है।",
    enterBirthTime: "पूरे चार्ट के लिए अपना जन्म समय लिखें।",
    timeOptionalHint: "पता न हो तो छोड़ दें। लग्न पर आधारित फल के लिए सही समय चाहिए।",
    timeUnknownToggle: "जन्म समय नहीं पता?",
    timeUnknownExplainer:
      "कोई बात नहीं — इसे खाली छोड़ दें, हम जन्म तिथि और स्थान से गणना करेंगे। सही जन्म समय के बिना हम सटीक लग्न, भाव, वर्ग चार्ट या दशा का समय नहीं बताएँगे, और चंद्रमा एक ही दिन में राशि या नक्षत्र बदल सकता है।",
    birthPlace: "जन्म स्थान",
    changePlace: "बदलें",
    placePlaceholder: "शहर लिखें, जैसे Delhi",
    pickCityHint: "सटीक निर्देशांक के लिए सुझावों में से शहर चुनें।",
    calculating: "गणना हो रही है...",
    yearTooEarly: "वर्ष 1900 या उसके बाद का होना चाहिए।",
    futureDate: "जन्म तिथि भविष्य की नहीं हो सकती।",
    invalidDate: "कृपया सही जन्म तिथि डालें।",
    defaultSubmit: "गणना करें",
    checkDetails: "ऊपर दिया जन्म विवरण जाँचकर दोबारा कोशिश करें।",
    pickCityToContinue: "आगे बढ़ने के लिए सुझावों में से अपना शहर चुनें।",
    enterName: "कृपया अपना नाम लिखें।",
    enterBirthDate: "कृपया अपनी जन्म तिथि डालें।",
    enterBirthPlace: "अपना जन्म स्थान लिखें।",
    placeNoMatch: "शहर नहीं मिला — ज़िला या पास का बड़ा शहर लिखें।",
    placeNoMatchDevanagari: "हिंदी नाम नहीं मिला — नाम अंग्रेज़ी में लिखें।",
  },
} as const;

export function birthFormLabels(language: "en" | "hi") {
  return BIRTH_FORM_LABELS[language];
}
